import { Flex } from "antd";
import { colors } from "shared/theme/color";
import { translate } from "shared/languages";
import { textPx } from "shared/constants/zoom";

interface ClassTypeSplitChartProps {
  openValue: number;
  privateValue: number;
  totalLabel: string;
  totalSuffix?: string;
  formatValue?: (value: number) => string;
}

const Legend = ({ color, label }: { color: string; label: string }) => (
  <Flex align="center" gap={6}>
    <span style={{ width: 8, height: 8, borderRadius: 999, background: color }} />
    <span style={{ fontSize: textPx(12), color: colors.ink2 }}>{label}</span>
  </Flex>
);

// A single proportion bar rather than a full axis-based chart (see
// chartUtils.ts) — two categories summing to a whole is exactly what a
// stacked bar communicates at a glance, no axis/gridlines needed. Generic
// over "value" (sessions, revenue, ...) so both split charts on this page
// share one component instead of two near-identical copies.
const ClassTypeSplitChart = ({
  openValue,
  privateValue,
  totalLabel,
  totalSuffix,
  formatValue = (value) => `${value}`,
}: ClassTypeSplitChartProps) => {
  const total = openValue + privateValue;
  const openPct = total ? Math.round((openValue / total) * 1000) / 10 : 0;
  // Complement of openPct (not independently rounded) so the two shares
  // always display as summing to exactly 100%, never 99.9% / 100.1%.
  const privatePct = total ? Math.round((100 - openPct) * 10) / 10 : 0;

  return (
    <div>
      <Flex justify="space-between" align="center" wrap="wrap" gap={12} style={{ marginBottom: 16 }}>
        <Flex gap={20}>
          <Legend color={colors.navy} label={translate("analyticsPage.class.type_open")} />
          <Legend color={colors.ink4} label={translate("analyticsPage.class.type_private")} />
        </Flex>
        {/* The total and the two figures under the bar are the numbers a
            reader checks against the KPI cards, so they are the one bold
            text on the card (chris, 2026-10-17); the layout stays. */}
        <span style={{ fontSize: textPx(12), fontWeight: 700, color: colors.ink }}>
          {totalLabel}: {formatValue(total)}{totalSuffix ? ` ${totalSuffix}` : ""}
        </span>
      </Flex>

      {total === 0 ? (
        <Flex align="center" justify="center" style={{ height: 28, color: colors.ink3, fontSize: textPx(13) }}>
          {translate("analyticsPage.sales.trend_empty")}
        </Flex>
      ) : (
        <>
          <Flex style={{ height: 14, borderRadius: 999, overflow: "hidden" }}>
            {openValue > 0 && (
              <div
                style={{ flex: openValue, background: colors.navy }}
                title={`${translate("analyticsPage.class.type_open")}: ${formatValue(openValue)} (${openPct}%)`}
              />
            )}
            {privateValue > 0 && (
              <div
                // Same deliberate visible grey as SessionsCompletedChart —
                // ink4, not the invisible bgSoftHover tint.
                style={{ flex: privateValue, background: colors.ink4 }}
                title={`${translate("analyticsPage.class.type_private")}: ${formatValue(privateValue)} (${privatePct}%)`}
              />
            )}
          </Flex>
          <Flex justify="space-between" style={{ marginTop: 8 }}>
            <span style={{ fontSize: textPx(12), fontWeight: 700, color: colors.ink }}>
              {formatValue(openValue)} ({openPct}%)
            </span>
            <span style={{ fontSize: textPx(12), fontWeight: 700, color: colors.ink }}>
              {formatValue(privateValue)} ({privatePct}%)
            </span>
          </Flex>
        </>
      )}
    </div>
  );
};

export default ClassTypeSplitChart;
